import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import WorkflowCanvas from "./WorkflowCanvas";
import WorkflowDrawer from "./WorkflowDrawer";
import {
  useNodesState,
  useEdgesState,
  addEdge,
  Node,
  Edge,
  Connection,
  ReactFlowProvider,
} from "reactflow";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWorkflowLinks,
  fetchWorkflowNodes,
  getWorkflowStatus,
  setUpdateStatusWorkflowStatus,
  updateWorkflow,
  updateWorkflowLinks,
} from "../../redux/slices/workflow/workflowSlice";
import { staticInitialNodes } from "./initialNodes";
import { StageDataProps } from "../reviewer/reviewForm";
import { createWorkflowLink } from "../../redux/slices/workflow";
import { setUpdateProjectStatus, updateProject } from "../../redux/slices/projectSlice/addProjectSlice";
import SnackbarNotification from "../snackbar/snackbarNotification";
import { getNodeStyle } from "./WorkflowStyle";
import DeleteModal from "../modal/deleteModal";
import { STATUS } from "../../utils/status";
import { NoWorkflowFound } from "./noWorkflowFoundNodes";
import { NoWorkflowFoundEdges } from "./noWorkflowFoundEdges";
import { setCreateReviewStatus } from "../../redux/slices/reviewerSlice";
import { MarkerType } from 'reactflow';

const END_NODE_ID = "end-node";
const START_NODE_ID = "start-node";



const WorkFlowPage: React.FC = () => {
  const { id: projectId = "" } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { nodes: workflowNodes, links: workflowLinks } = useSelector(
    (state: RootState) => state.workflow
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);

  const [stageId, setStageId] = useState<string | null>(null);
  const [stageData, setStageData] = useState<StageDataProps>(
    {} as StageDataProps
  );
  const [openDrawer, setOpenDrawer] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: 'info' as 'success' | 'error' | 'info' | 'warning',
  });
  const [loading, setLoading] = useState(true);
  const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const updateStatus = useSelector((state: RootState) => state.projects.updateStatus)
  const updateMessage = useSelector((state: RootState) => state.projects.updateProjectMessage)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [specialNodePositions, setSpecialNodePositions] = useState({ start: { x: -200, y: -240 }, end: { x: -200, y: 120 } });
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const createStatus = useSelector((state: RootState) => state.reviewStage.createReviewStatus);
  const createMessage = useSelector((state: RootState) => state.reviewStage.createReviewMessage);
  const updateStatusWorkflow = useSelector((state: RootState) => state.workflow.updateStatusWorkflow);
  const updateStageStatus = useSelector((state: RootState) => state.reviewStage.updateReviewStatus);
  const { links, linksLoader, error } = useSelector((state: any) => state.workflow);

  const { workflowStatus, workflowStatusLoading, workflowStatusError } = useSelector((state: any) => state.workflow);


  useEffect(() => {
    if (projectId) {
      dispatch(getWorkflowStatus(projectId));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if ((updateStatusWorkflow === STATUS.SUCESS || updateStageStatus === STATUS.SUCESS) && projectId) {
      dispatch(fetchWorkflowNodes(projectId));
      dispatch(fetchWorkflowLinks(projectId));
    }
  }, [updateStatusWorkflow, updateStageStatus, dispatch, projectId]);

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      Promise.all([
        dispatch(fetchWorkflowNodes(projectId)),
        dispatch(fetchWorkflowLinks(projectId)),
      ]).finally(() => setLoading(false));
    }
  }, [dispatch, projectId]);

  const styledNodes = nodes.map((node) => ({
    ...node,
    style: {
      ...(node.style || {}),
      ...getNodeStyle(
        node.data && "type" in node.data ? { type: (node.data as any).type, status: (node.data as any).status } : undefined
      ),
    },
  }));

  useEffect(() => {
    if (!workflowNodes.length) return;

    const fetchedNodes: Node[] = workflowNodes.map((n: any) => ({
      id: n.id.toString(),
      type: "default",
      position: {
        x: n.position_x ?? 0,
        y: n.position_y ?? 0,
      },
      data: {
        label: n.name,
        ...n,
      },
    }));

    // define your start and end nodes
    const startNode: Node = {
      id: START_NODE_ID,
      type: "input",
      position: specialNodePositions.start,
      draggable: true,
      data: { label: "Start", type: "start", id: START_NODE_ID },
    };

    const endNode: Node = {
      id: END_NODE_ID,
      type: "output",
      position: specialNodePositions.end,
      draggable: true,
      data: { label: "Ready to Deliver", type: "end", id: END_NODE_ID },
    };

    const horizontalSpacing = 250; // space between nodes
    const centerY = (specialNodePositions.start.y + specialNodePositions.end.y) / 2;

    // detect if all nodes have default/null positions
    const allPositionsEmpty = fetchedNodes.every(
      (n) => !n.position.x && !n.position.y
    );

    const positionedNodes = allPositionsEmpty
      ? fetchedNodes.map((node, index) => ({
        ...node,
        position: {
          x: index * horizontalSpacing - ((fetchedNodes.length - 1) * horizontalSpacing) / 2,
          y: centerY,
        },
      }))
      : fetchedNodes.map((n, index) => ({
        ...n,
        position: {
          x:
            n.position.x && n.position.x !== 0
              ? n.position.x
              : index * horizontalSpacing - ((fetchedNodes.length - 1) * horizontalSpacing) / 2,
          y:
            n.position.y && n.position.y !== 0
              ? n.position.y
              : centerY,
        },
      }));

    // Combine with start and end nodes
    const allNodesMap = new Map<string, Node>();
    [startNode, ...positionedNodes, endNode].forEach((node) => {
      allNodesMap.set(node.id, node);
    });
    const allNodes = Array.from(allNodesMap.values());

    // Build edges (unchanged)
    const initialEdges: Edge[] = workflowLinks
      .filter((link: any) => link.start_stage_id || link.end_stage_id)
      .map((link) => ({
        id: link.id.toString(),
        source:
          link.start_stage_id === null
            ? START_NODE_ID
            : link.start_stage_id.toString(),
        target:
          link.end_stage_id === null
            ? END_NODE_ID
            : link.end_stage_id.toString(),
        type: "smoothstep",
        animated: true,
        style: { stroke: "#3D85C6", strokeWidth: 2, strokeDasharray: "4 2" },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#3D85C6" },
      }));

    setNodes(allNodes);
    setEdges(initialEdges);
  }, [workflowNodes, workflowLinks, specialNodePositions]);


  const onConnect = useCallback(
    (params: Connection) => {
      const sourceId: string | null = params.source ?? null;
      const targetId: string | null = params.target ?? null;

      if (!sourceId || !targetId) return;

      if (sourceId === targetId) {
        setSnackbar({
          open: true,
          message: "You cannot connect a node to itself.",
          severity: "warning",
        });
        return;
      }

      const alreadyHasOutput = edges.some((edge) => edge.source === sourceId);
      if (alreadyHasOutput && sourceId !== START_NODE_ID) {
        setSnackbar({
          open: true,
          message: "A node can have only one output connection.",
          severity: "warning",
        });
        return;
      }

      if (sourceId && sourceId !== END_NODE_ID && sourceId !== START_NODE_ID) {
        const existingEndConnection = edges.some(
          (edge) => edge.source === sourceId && edge.target === END_NODE_ID
        );
        if (existingEndConnection) {
          setSnackbar({
            open: true,
            message:
              "A node connected to 'Ready to Deliver' can have only one output connection.",
            severity: "warning",
          });
          return;
        }
      }

      const toInt = (val: string | null): number | null =>
        val && !isNaN(Number(val)) ? parseInt(val) : null;

      let start_stage_id: number | null = toInt(sourceId);
      let end_stage_id: number | null = toInt(targetId);

      if (sourceId === START_NODE_ID) {
        start_stage_id = null;
        end_stage_id = toInt(targetId);
      }

      if (targetId === END_NODE_ID) {
        start_stage_id = toInt(sourceId);
        end_stage_id = null;
      }

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            type: "smoothstep",
            style: {
              stroke: "#3D85C6",
              strokeWidth: 2,
              strokeDasharray: "4 2",
            },
            labelStyle: { fill: "#333", fontSize: 12 },
            markerEnd: { type: MarkerType.ArrowClosed, color: "#3D85C6" },
          },
          eds
        )
      );

      if (sourceId === START_NODE_ID && targetId) {
        dispatch(
          updateWorkflow({
            project_id: projectId,
            id: parseInt(targetId),
            start_stage: true,
          })
        );
      }

      if (targetId === END_NODE_ID && sourceId) {
        dispatch(
          updateWorkflow({
            project_id: projectId,
            id: parseInt(sourceId),
            end_stage: true,
          })
        );
      }

      if (projectId && sourceId && targetId) {
        const payload = {
          project_id: projectId,
          page_size: 100,
          data: [
            {
              name: `Link_${sourceId}_to_${targetId}`,
              start_stage_id,
              end_stage_id,
            },
          ],
        };
        dispatch(createWorkflowLink(payload));
      }

      const connectedNodeIds = [sourceId, targetId].filter(
        (id) => id !== END_NODE_ID && id !== START_NODE_ID
      );
      const updatedNodes = nodes.filter((node) =>
        connectedNodeIds.includes(node.id)
      );

      updatedNodes.forEach((node) => {
        dispatch(
          updateWorkflow({
            project_id: projectId,
            id: parseInt(node.id),
            position_x: node.position.x,
            position_y: node.position.y,
            paginate: false,
          })
        );
      });
    },
    [dispatch, projectId, nodes, edges]
  );

  const onNodeClick = (event: React.MouseEvent, node: Node) => {
    if (node.data?.id && node.data?.id !== 'end-node' && node.data?.id !== 'start-node') {
      setStageId(node.data.id);
      setStageData(node.data);
      setOpenDrawer(true);
    }
  };

  useEffect(() => {
    const handleCreateStatus = () => {
      if (createStatus === STATUS.FAILED) {
        handleSnackbar(createMessage, 'error');
      }
    };

    handleCreateStatus();
    return () => {
      if (createStatus === STATUS.SUCESS || createStatus === STATUS.FAILED) {
        dispatch(setCreateReviewStatus(STATUS.NOT_STARTED));
      }
    };
  }, [createStatus, dispatch]);

  useEffect(() => {
    const handleUpdateWorkflowStatus = () => {
      if (updateStatusWorkflow === STATUS.FAILED) {
        //handleSnackbar(createMessage, 'error');
        dispatch(setUpdateStatusWorkflowStatus(STATUS.NOT_STARTED));
      }
      else if (updateStatusWorkflow === STATUS.SUCESS) {
        dispatch(setUpdateStatusWorkflowStatus(STATUS.NOT_STARTED));
      }
    };

    handleUpdateWorkflowStatus();
  }, [updateStatusWorkflow, dispatch]);

  const onEdgeClick = (event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();

    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    const sourceLabel =
      typeof (sourceNode?.data as { label?: string })?.label === 'string'
        ? (sourceNode?.data as { label?: string }).label
        : 'Source';

    const targetLabel =
      typeof (targetNode?.data as { label?: string })?.label === 'string'
        ? (targetNode?.data as { label?: string }).label
        : 'Target';

    setDeleteMessage(
      `Are you sure you want to delete the edge between "${sourceLabel}" and "${targetLabel}"?`
    );
    setSelectedEdge(edge);
    setIsEdgeModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedEdge) {
      const numericId = parseInt(selectedEdge.id);
      setEdges((prev) => prev.filter((e) => e.id !== selectedEdge.id));
      dispatch(updateWorkflowLinks({ id: numericId, is_deleted: true }));
    }
    setIsEdgeModalOpen(false);
  }

  const handleSubmit = () => {
    const payload = {
      id: projectId,
      workflow_status: "ACTIVE",
    };
    dispatch(updateProject(payload));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (updateStatus === STATUS.SUCESS) {
      handleSnackbar(updateMessage, 'success');
      dispatch(setUpdateProjectStatus(STATUS.NOT_STARTED));
    }

    if (updateStatus === STATUS.FAILED) {
      handleSnackbar(updateMessage, 'error');
      dispatch(setUpdateProjectStatus(STATUS.NOT_STARTED));
    }
  }, [updateStatus, dispatch, updateMessage]);

  const handleNodeDragStop = useCallback((event: React.MouseEvent, node: Node) => {
    const numericId = Number(node.id);
    if (node.id === START_NODE_ID) {
      setSpecialNodePositions((prev) => ({
        ...prev,
        start: { x: node.position.x, y: node.position.y },
      }));
      return;
    }

    if (node.id === END_NODE_ID) {
      setSpecialNodePositions((prev) => ({
        ...prev,
        end: { x: node.position.x, y: node.position.y },
      }));
      return;
    }
    if (!Number.isNaN(numericId) && projectId) {
      const payload = {
        project_id: projectId,
        id: numericId,
        position_x: node.position.x,
        position_y: node.position.y,
        page_size: 100,
      };
      dispatch(updateWorkflow(payload));
    }
  },
    [dispatch, projectId]
  );


  useEffect(() => {
    if (!workflowNodes || workflowNodes.length === 0) return;
    const startNode = workflowNodes.find((node) => node.start_stage);
    const endNode = workflowNodes.find((node) => node.end_stage);

    const firstNode = startNode || workflowNodes[0];
    const lastNode = endNode || workflowNodes[workflowNodes.length - 1];

    const newPositions = {
      start: {
        x: firstNode.position_x ?? 0,
        y: (firstNode.position_y ?? 0) - 150,
      },
      end: {
        x: lastNode.position_x ?? 0,
        y: (lastNode.position_y ?? 0) + 150,
      },
    };

    setSpecialNodePositions(newPositions);
  }, [workflowNodes]);
  

  return (
    <Box style={{ height: "100vh", position: "relative" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {workflowNodes?.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Button variant="contained" color="primary" onClick={handleSubmit}>
                Save
              </Button>
              <Box
                component="span"
                sx={{
                  backgroundColor: "#E0F7FA",
                  color: "#040079ff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {workflowStatusLoading ? "Loading ..." : `${workflowStatus}`}
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1, height: "100%" }}>
            <ReactFlowProvider>
              <WorkflowCanvas
                nodes={
                  workflowNodes.length
                    ? styledNodes.map((n) => ({
                      ...n,
                      type: "customNode",
                    }))
                    : NoWorkflowFound
                }
                edges={workflowNodes.length ? edges : NoWorkflowFoundEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onNodeDragStop={handleNodeDragStop}
              />
            </ReactFlowProvider>
          </Box>
        </>
      )}

      <WorkflowDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        projectId={projectId}
        stageId={stageId}
        stageData={stageData}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />

      <DeleteModal
        open={isEdgeModalOpen}
        onCancel={() => setIsEdgeModalOpen(false)}
        onConfirm={confirmDelete}
        confirmationMessage={deleteMessage}
      />

      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default WorkFlowPage;
